import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Accordion, AccordionSummary, AccordionDetails,
  Typography, Checkbox, Divider, Button, Box, CircularProgress,
  ListItem, ListItemText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { includeInsencitive } from 'util/object';
import Produto from 'entity/Produto';
import { fetchArray } from 'entity';
import { FixedSizeList as VirtualizedList } from 'react-window';

// Agrupamento e ordenação
const agruparPorGrupo = (lista, selecionadosTemp, produtoJaAdicionado) => {
  const grupos = Object.entries(
    (lista || []).reduce((acc, detalhamento) => {
      const grupoNome = detalhamento.produto.grupo?.nome || 'Sem Grupo';
      if (!acc[grupoNome]) acc[grupoNome] = [];
      acc[grupoNome].push({ ...detalhamento, indexList: lista.indexOf(detalhamento) });
      return acc;
    }, {})
  );
  // Ordena: grupos com algum produto selecionado (temporário ou já adicionado) primeiro
  return grupos.sort(([aNome, aDetalhamentos], [bNome, bDetalhamentos]) => {
    const aSelecionado = aDetalhamentos.some(d => produtoJaAdicionado(d) || (selecionadosTemp && selecionadosTemp.some(p => p.produto.uid === d.produto.uid)));
    const bSelecionado = bDetalhamentos.some(d => produtoJaAdicionado(d) || (selecionadosTemp && selecionadosTemp.some(p => p.produto.uid === d.produto.uid)));
    if (aSelecionado === bSelecionado) {
      return aNome.localeCompare(bNome, 'pt-BR');
    }
    return aSelecionado ? -1 : 1;
  });
};

export default function ProdutoSelectorDialog({
  open,
  onClose,
  isMobile,
  produtosPermitidos,
  produtosSelecionados,
  onAdicionarProdutos
}) {
  const [todosProdutos, setTodosProdutos] = useState(produtosPermitidos);
  const [filtro, setFiltro] = useState('');
  const [selecionadosTemp, setSelecionadosTemp] = useState([]);
  const [carregando, setCarregando] = useState(false);
  // Estado para controlar quais grupos estão expandidos
  const [expandedGroup, setExpandedGroup] = useState(null);
  // refs para cada grupo
  const groupRefs = useRef({});

  useEffect(() => {
    if (!produtosPermitidos) {
      setCarregando(true);
      fetchArray(Produto.getAllAtivo()).then(result => {
        setTodosProdutos(result.map(p => ({
          uid: p.uid,
          produto: p,
          descricaoRodada: p.descricao || null,
        })));
        setCarregando(false);
      });
    } else {
      setTodosProdutos(produtosPermitidos);
    }
  }, [produtosPermitidos]);

  const produtosFiltrados = useMemo(
    () => (todosProdutos || []).filter(
      (d) =>
        includeInsencitive(d.produto.nome, filtro) ||
        includeInsencitive(d.produto.grupo?.nome || '', filtro)
    ),
    [todosProdutos, filtro]
  );
  const produtoJaAdicionado = useCallback(
    (detalhamento) =>
      (produtosSelecionados || []).some((p) => p.produto.uid === detalhamento.produto.uid),
    [produtosSelecionados]
  );
  const produtosFiltradosAgrupados = useMemo(
    () => agruparPorGrupo(produtosFiltrados, selecionadosTemp, produtoJaAdicionado),
    [produtosFiltrados, selecionadosTemp, produtoJaAdicionado]
  );

  // Função para rolar até o grupo expandido
  const scrollToGroup = (grupoNome) => {
    const ref = groupRefs.current[grupoNome];
    if (ref && ref.scrollIntoView) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const toggleSelecionadoTemp = (detalhamento, grupoNome) => {
    setSelecionadosTemp((prev) => {
      const existe = prev.find((p) => p.produto.uid === detalhamento.produto.uid);
      setTimeout(() => scrollToGroup(grupoNome), 100); // espera reordenar
      return existe
        ? prev.filter((p) => p.produto.uid !== detalhamento.produto.uid)
        : [...prev, detalhamento];
    });
  };

  const toggleTodosGrupo = (detalhamentos, grupoNome) => {
    const disponiveis = detalhamentos.filter(
      d => !produtoJaAdicionado(d)
    );
    const todosSelecionados = disponiveis.every(d =>
      selecionadosTemp.some(p => p.produto.uid === d.produto.uid)
    );
    setExpandedGroup(grupoNome);
    if (!todosSelecionados) {
      // Expande o grupo se não estiver expandido
      setTimeout(() => scrollToGroup(grupoNome), 100); // espera reordenar
      setSelecionadosTemp(prev => [
        ...prev,
        ...disponiveis.filter(d => !prev.some(p => p.produto.uid === d.produto.uid))
      ]);
    } else {
      setTimeout(() => scrollToGroup(grupoNome), 100); // espera reordenar
      setSelecionadosTemp(prev =>
        prev.filter(
          p => !disponiveis.some(grD => grD.produto.uid === p.produto.uid)
        )
      );
    }
  };

  const selecionarTodos = () => {
    const todosDisponiveis = produtosFiltrados.filter(d => !produtoJaAdicionado(d));
    setSelecionadosTemp(prev => {
      // Remove produtos que já estão na seleção temporária
      const novosParaAdicionar = todosDisponiveis.filter(d =>
        !prev.some(p => p.produto.uid === d.produto.uid)
      );
      return [...prev, ...novosParaAdicionar];
    });
  };

  const desmarcarTodos = () => {
    setSelecionadosTemp([]);
  };

  const confirmarSelecao = () => {
    onAdicionarProdutos(selecionadosTemp);
    setSelecionadosTemp([]);
    setFiltro('');
    onClose();
  };

  if (carregando || todosProdutos === undefined) {
    return <Box p={3} display="flex" justifyContent="center"><CircularProgress /></Box>;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: isMobile
          ? { m: 0, borderRadius: 0, height: '100dvh' }
          : { width: '88vw', maxWidth: '1100px', minWidth: '700px' },
      }}
    >
      <DialogTitle
        sx={{
          pb: 0,
          fontSize: 18,
          ...(isMobile && { position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 10 }),
        }}
      >
        Selecionar Produto
      </DialogTitle>
      <DialogContent sx={{
        display: 'flex',
        flexDirection: 'column',
        p: isMobile ? 1.5 : 3,
        pt: isMobile ? 0 : 3,
        ...(isMobile ? { height: 'calc(100dvh - 70px)', maxHeight: 'none' } : { maxHeight: '80vh' }),
      }}>
        <Box mb={1}>
          <input
            autoFocus
            type="text"
            placeholder="Buscar por nome ou grupo"
            value={filtro}
            onChange={e => setFiltro(e.target.value)}
            style={{
              width: '100%',
              padding: isMobile ? '8px' : '12px',
              fontSize: isMobile ? '1rem' : '1.1rem',
              borderRadius: 6,
              border: '1px solid #ddd',
              outline: 0,
            }}
          />
        </Box>

        {/* Botões para selecionar/desmarcar todos */}
        <Box sx={{
          display: 'flex',
          gap: 1,
          mb: 1,
          flexWrap: 'wrap'
        }}>
          <Button
            variant="outlined"
            size="small"
            onClick={selecionarTodos}
            disabled={produtosFiltrados.filter(d => !produtoJaAdicionado(d)).length === 0}
            sx={{
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              py: 0.5
            }}
          >
            Selecionar Todos
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={desmarcarTodos}
            disabled={selecionadosTemp.length === 0}
            sx={{
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              py: 0.5
            }}
          >
            Desmarcar Todos
          </Button>
          {selecionadosTemp.length > 0 && (
            <Typography
              variant="caption"
              sx={{
                alignSelf: 'center',
                ml: 1,
                color: 'primary.main',
                fontSize: isMobile ? '0.7rem' : '0.75rem'
              }}
            >
              {selecionadosTemp.length} produto{selecionadosTemp.length !== 1 ? 's' : ''} selecionado{selecionadosTemp.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: isMobile ? 1 : 2 }} />
        <Box sx={{
          overflowY: 'auto',
          flex: 1,
          pb: isMobile ? 8 : 0,
        }}>
          {produtosFiltradosAgrupados.length > 0 ? (
            produtosFiltradosAgrupados.map(([grupoNome, detalhamentos], idx) => {
              const disponiveis = detalhamentos.filter(d => !produtoJaAdicionado(d));
              const todosSelecionados =
                disponiveis.length > 0 &&
                disponiveis.every(d =>
                  selecionadosTemp.some(p => p.produto.uid === d.produto.uid)
                );
              const algumSelecionado = disponiveis.some(
                d => selecionadosTemp.some(p => p.produto.uid === d.produto.uid)
              );
              // Só o primeiro grupo fica expandido por padrão
              const isExpanded = expandedGroup === grupoNome
              return (
                <Accordion
                  key={grupoNome}
                  expanded={isExpanded}
                  onChange={() => setExpandedGroup(isExpanded ? null : grupoNome)}
                  disableGutters
                  sx={{ mb: 1 }}
                  ref={el => groupRefs.current[grupoNome] = el}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
                    sx={{
                      minHeight: 36,
                      maxHeight: 48,
                      '& .MuiAccordionSummary-content': {
                        margin: 0,
                        alignItems: 'center',
                        display: 'flex',
                        overflow: 'hidden',
                        flex: 1,
                        paddingLeft: 1
                      },
                      '& .MuiAccordionSummary-expandIconWrapper': {
                        flexShrink: 0
                      },
                      px: 1,
                    }}
                  >
                    <Checkbox
                      edge="start"
                      indeterminate={algumSelecionado && !todosSelecionados}
                      checked={detalhamentos.length > 0 && detalhamentos.every(d => produtoJaAdicionado(d) || selecionadosTemp.some(p => p.produto.uid === d.produto.uid))}
                      onChange={() => toggleTodosGrupo(detalhamentos, grupoNome)}
                      onClick={e => e.stopPropagation()}
                      disabled={false}
                      icon={algumSelecionado && !todosSelecionados ? <IndeterminateCheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                      checkedIcon={<CheckBoxIcon />}
                      sx={{ mr: 1, p: 0.5, flexShrink: 0 }}
                    />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        lineHeight: 1.3,
                        fontSize: 15,
                        fontWeight: 500,
                        verticalAlign: 'middle',
                        wordBreak: 'break-word',
                        whiteSpace: 'wrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        flex: 1,

                      }}
                      title={grupoNome}
                    >
                      {grupoNome}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ py: 0.5, px: 1 }}>
                    <VirtualizedList
                      height={Math.min(300, detalhamentos.length * 48)}
                      itemCount={detalhamentos.length}
                      itemSize={48}
                      width="100%"
                      itemData={{
                        detalhamentos,
                        produtoJaAdicionado,
                        selecionadosTemp,
                        toggleSelecionadoTemp,
                        isMobile,
                        grupoNome
                      }}
                      style={{ maxWidth: '100%' }}
                    >
                      {({ index, style, data }) => {
                        const d = data.detalhamentos[index];
                        const jaAdicionado = data.produtoJaAdicionado(d);
                        const selecionado = jaAdicionado || data.selecionadosTemp.some((p) => p.produto.uid === d.produto.uid);
                        return (
                          <ListItem
                            key={d.produto.uid}
                            disabled={jaAdicionado}
                            button
                            onClick={() => data.toggleSelecionadoTemp(d, data.grupoNome)}
                            sx={{ py: 0 }}
                            style={style}
                          >
                            <Checkbox
                              edge="start"
                              checked={selecionado}
                              tabIndex={-1}
                              disableRipple
                              disabled={false}
                            />
                            <ListItemText

                              primary={d.produto.nome}
                              secondary={d.descricaoRodada || ''}
                              sx={{
                                whiteSpace: 'wrap',
                                '.MuiListItemText-secondary': { fontSize: data.isMobile ? 10 : 11 }
                              }}
                            />
                          </ListItem>
                        );
                      }}
                    </VirtualizedList>
                  </AccordionDetails>
                </Accordion>
              );
            })
          ) : (
            <Typography variant="body2" textAlign="center" color="textSecondary" mt={2}>
              Nenhum produto encontrado.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{
        px: isMobile ? 2 : 3,
        pb: isMobile ? 2 : 3,
        position: isMobile ? 'fixed' : 'static',
        left: 0, right: 0, bottom: 0,
        bgcolor: isMobile ? "background.paper" : undefined,
        borderTop: isMobile ? `1px solid #eee` : undefined,
        zIndex: 20
      }}>
        <Button onClick={onClose} sx={{ mr: 2 }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={confirmarSelecao}
          fullWidth={isMobile}
        >
          Adicionar Selecionados
        </Button>
      </DialogActions>
    </Dialog>
  );
}